import { useEffect, useState } from "react";

import { PatientPortalClient } from "@/Common/Api/Patient/PatientPortalClient";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { SharedItemPatientSendingSettings } from "@/Common/Models/Patient/SharedItemPatientSendingSettings";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LinkButton } from "@/DesignSystem";
import { BlockPatientMessagingPopup } from "./BlockPatientMessagingPopup";

import styles from "./PatientMessaging.module.scss";

interface IPatientMessagingProps {
    patientContactId: string;
    headerLabel?: string;
    messagesLimitLabel?: string;
    alignBlockedReason?: boolean;
    highlightContent?: boolean;
}

function PatientMessaging(props: Readonly<IPatientMessagingProps>): JSX.Element {
    const { getText } = useTranslation();
    const [settings, setSettings] = useState<SharedItemPatientSendingSettings>();
    const [displayBlockPopup, setDisplayBlockPopup] = useState<boolean>(false);

    const toggleBlockStatus = async (isBlocked: boolean, reason?: string): Promise<void> => {
        const response = await PatientPortalClient.toggleBlockedStatus(settings?.patientContactFacetId, isBlocked, reason);

        if (!response) {
            return;
        }

        setSettings(
            p =>
                new SharedItemPatientSendingSettings(
                    {
                        ...p,
                        blockedByUserId: response.blockedByUserId,
                        blockedDate: response.blockedDate,
                        blockedReason: response.blockedReason
                    }
                )
        );
    };

    const hasPatientMessagesBlocked = (): boolean => !!settings?.blockedDate;

    const getBlockedStatusSpan = (): JSX.Element => !hasPatientMessagesBlocked() ?
        (<span>{getText("ActiveLabel")}</span>) :
        (<span className={styles.errorLabel}>{getText("SharedItem_BlockedLabel")}</span>);

    const getActionButton = (): JSX.Element => !hasPatientMessagesBlocked() ?
        (<LinkButton variant="error" label={getText("SharedItem_BlockLabel")} onClick={() => { setDisplayBlockPopup(true); }} />) :
        (<LinkButton label={getText("SharedItem_UnblockLabel")} onClick={() => { toggleBlockStatus(false); }} />);

    const formatDate = (date: Date): string => LuxonDateTimeParser.parse(date)?.toFormat(`yyyy/MM/dd '${getText("AtLabel")}' HH:mm`);
    const highlightContent = `${styles.content} ${props.highlightContent ? styles.bold : ""}`;

    const renderBlockedReason = (): JSX.Element => {
        if (!hasPatientMessagesBlocked()) {
            return null;
        }

        const italicLabel = `${styles.italicLabel} ${props.alignBlockedReason ? styles.left : ""}`;
        const blockedByUserId = cacheStore.GetEmployeeByUserId(settings?.blockedByUserId);
        const blockedDate = settings?.blockedDate ? formatDate(settings.parsedBlockedDate) : "";

        const renderContent = (): JSX.Element => (
            <>
                <div className={`${styles.errorLabel} ${italicLabel}`}>
                    {`${getText("SharedItem_BlockedByLabel")} ${blockedByUserId ? NameHelper.formatFullNameWithPrefixForHasName(blockedByUserId) : ""} ${getText("OnLabel")} ${blockedDate}`}
                </div>
                <div className={italicLabel}>
                    {settings?.blockedReason}
                </div>
            </>
        );

        return (
            <div className={props.alignBlockedReason ? styles.labelRow : styles.columnTwo}>
                {props.alignBlockedReason && (
                    <>
                        <div className={styles.label} />
                        <div className={styles.alignedBlockedContainer}>{renderContent()}</div>
                    </>
                )}
                {!props.alignBlockedReason && renderContent()}
            </div>
        );
    };

    useEffect(() => {
        let active = true;

        const fetchPatientSendingSettings = async (): Promise<void> => {
            const response = await PatientPortalClient.getPatientSendingSettings(props.patientContactId);

            if (!active) {
                return;
            }

            setSettings(response);
        };

        fetchPatientSendingSettings();

        return () => {
            active = false;
        };
    }, [props.patientContactId]);

    return (
        <>
            <div className={styles.patientMessagingContainer}>
                <div className="flex-container heading">
                    <h3 className="h thin-font">{props.headerLabel ?? getText("SharedItem_PatientMessagingLabel")}</h3>
                </div>
                <div className={styles.gridRow}>
                    <div className={styles.columnOne}>
                        <div className={styles.labelRow}>
                            <div className={styles.label}>
                                {getText("StatusLabel")}
                            </div>
                            <div className={highlightContent}>
                                {getBlockedStatusSpan()}
                            </div>
                            <div className={styles.actionButton}>
                                {getActionButton()}
                            </div>
                        </div>
                        {props.alignBlockedReason && renderBlockedReason()}
                        {cacheStore.ClinicSettings.EnablePatientInitiatedBidirectionalCommunication && (
                            <div className={styles.labelRow}>
                                <div className={styles.label}>
                                    {props.messagesLimitLabel ?? getText("SharedItem_MessagesRemainingLabel")}
                                </div>
                                <div className={highlightContent}>{settings?.remainingMessages}</div>
                                <div className={styles.actionButton}>
                                    {settings?.patientContactFacetId && (
                                        <IncreaseMessagesLimitButton
                                            patientContactFacetId={settings.patientContactFacetId}
                                            messagesLimit={settings.messagesLimit}
                                            onSuccess={(newMessagesLimit, newRemainingMessages) => {
                                                setSettings(
                                                    p => new SharedItemPatientSendingSettings({
                                                        ...p,
                                                        messagesLimit: newMessagesLimit,
                                                        remainingMessages: newRemainingMessages
                                                    })
                                                );
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {!props.alignBlockedReason && renderBlockedReason()}
                </div>
            </div>
            {displayBlockPopup && (
                <BlockPatientMessagingPopup
                    onCancelClick={() => setDisplayBlockPopup(false)}
                    onBlockClick={reason => {
                        setDisplayBlockPopup(false);
                        toggleBlockStatus(true, reason);
                    }}
                />
            )}
        </>
    );
}

function IncreaseMessagesLimitButton({ patientContactFacetId, messagesLimit, onSuccess }: Readonly<{
    patientContactFacetId: string;
    messagesLimit: number,
    onSuccess: (newMessagesLimit: number, newRemainingMessages: number) => void;
}>): JSX.Element {
    const { getText } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const getMessagesIncrement = (): number => {
        const defaultQuota = cacheStore.ClinicSettings.BidirectionalCommunicationPatientMessageLimit;

        if (defaultQuota >= 9) return 5;
        if (defaultQuota >= 7) return 4;
        if (defaultQuota >= 5) return 3;
        if (defaultQuota >= 3) return 2;
        return 1;
    };

    const messagesLimitIncrement = getMessagesIncrement();

    const increaseRemainingMessages = async (): Promise<void> => {
        setIsLoading(true);

        try {
            const response = await PatientPortalClient.increasePatientMessagesLimit(patientContactFacetId, messagesLimit, messagesLimitIncrement);

            if (!response) {
                return;
            }

            onSuccess(response.MessagesLimit, response.RemainingMessages);
        }
        finally {
            setIsLoading(false);
        }
    };

    return (
        <LinkButton
            onClick={increaseRemainingMessages}
            label={`${getText("AddLabel")} ${messagesLimitIncrement}`}
            disabled={isLoading}
        />
    );
}

export { PatientMessaging };
